import {useState} from 'react';
import doctorViewingCase from '@legit-health/svg-images/images/doctor-viewing-case.svg';
import empowerPatient from '@legit-health/svg-images/images/empower-patient.svg';
import patientTakingSelfie from '@legit-health/svg-images/images/patient-taking-selfie.svg';
import {generatePath} from '@legit-health/ui';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {Alert, Box, Button, MobileStepper, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {useCurrentPatient} from '@/patient/context/CurrentPatientContext';
import useCanUploadPhotos from '@/patient/hooks/useCanUploadPhotos';
import useOpenUploadPhoto from '@/patient/hooks/useOpenUploadPhoto';
import Patient from '@/patient/interfaces/Patient';
import {UPDATE_PROFILE} from '@/shared/const/backend';
import useUserContext, {useCompany} from '@/shared/contexts/UserContext/useUserContext';
import useApiMutation from '@/shared/hooks/useApiMutation';
import AuthTokens from '@/shared/interfaces/AuthTokens';

const StyledImage = styled('img')({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain'
});

const steps = [
  {
    id: 'one',
    image: doctorViewingCase
  },
  {
    id: 'two',
    image: empowerPatient
  },
  {
    id: 'three',
    image: patientTakingSelfie
  }
];

interface MutationResponse {
  authTokens: AuthTokens;
  user: Patient;
}

export default function OnBoarding() {
  const {isPatientOnBoardingEnabled} = useCompany();
  const patient = useCurrentPatient();
  const canUploadPhotos = useCanUploadPhotos();
  const openUploadPhoto = useOpenUploadPhoto();
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = steps.length;
  const hasFinished = activeStep === maxSteps - 1;
  const {t} = useTranslation(['ui', 'shared/common', 'patient/on_boarding']);
  const {mutate: patchProfileMutation, isPending, isSuccess} = useApiMutation<MutationResponse>();
  const {fetchUser} = useUserContext();
  const [error, setError] = useState(false);

  if (patient.hasCompletedTutorial || !isPatientOnBoardingEnabled || !canUploadPhotos) {
    return null;
  }

  function handleNext() {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }

  function handleBack() {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  function start() {
    patchProfileMutation(
      {
        method: 'PATCH',
        path: generatePath(UPDATE_PROFILE, {id: patient.id}),
        body: {
          hasCompletedTutorial: true
        }
      },
      {
        onSuccess: function () {
          fetchUser();
        },
        onError: () => setError(true)
      }
    );
    openUploadPhoto({
      onSuccess: () => fetchUser()
    });
  }

  const activeStepObject = steps[activeStep];

  if (!activeStepObject) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 'modal',
          backgroundColor: 'common.white'
        }}
      >
        <Box
          data-testid="onBoardingBox"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <Box sx={{p: 2}}>
            <Box sx={{mb: 2, height: '250px'}}>
              <StyledImage
                src={activeStepObject.image}
                alt={t(`patient/on_boarding:${activeStepObject.id}.title`)}
              />
            </Box>

            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: '16px'
              }}
            >
              {t(`patient/on_boarding:${activeStepObject.id}.title`)}
            </Typography>
            <Typography
              sx={{
                textAlign: 'center',
                mb: '16px'
              }}
            >
              {t(`patient/on_boarding:${activeStepObject.id}.text`)}
            </Typography>
            <Button
              onClick={hasFinished ? start : handleNext}
              variant="contained"
              color="primary"
              fullWidth
              disableElevation
              size="large"
              endIcon={<NavigateNextIcon />}
              disabled={isPending || isSuccess}
              data-testid="onBoardingButton"
            >
              {hasFinished
                ? isPending || isSuccess
                  ? t('shared/common:loading')
                  : t('shared/common:start')
                : t('ui:next')}
            </Button>
            {error && <Alert severity="error">{t('patient/on_boarding:error')}</Alert>}
          </Box>
          <Box sx={{p: 2}}>
            <MobileStepper
              variant="dots"
              steps={maxSteps}
              position="static"
              activeStep={activeStep}
              nextButton={
                <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1}>
                  <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                  <KeyboardArrowLeft />
                </Button>
              }
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}
